import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/store";
import { usePermit, useUpdatePermit } from "@/hooks/use-permits";
import { useFacilities } from "@/hooks/use-facilities";
import { useToast } from "@/hooks/use-toast";
import { PermitForm } from "@/components/permits/permit-form";
import { PageHeader, Breadcrumbs, PageSkeleton } from "@/components/ui";

export const Route = createFileRoute("/_authenticated/permits/$permitId/edit")({
  component: PermitEditPage,
});

function PermitEditPage() {
  const { t } = useTranslation("permits");
  const { permitId } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const orgId = useAppStore((s) => s.activeOrgId);

  const { data: permit, isLoading: permitLoading } = usePermit(orgId, permitId);
  const { data: facilitiesData, isLoading: facilitiesLoading } = useFacilities(orgId, {
    limit: 20,
    status: "active",
  });
  const facilities = facilitiesData?.data ?? [];

  const updateMutation = useUpdatePermit(orgId, permitId);

  if (permitLoading || facilitiesLoading) {
    return (
      <div className="mx-auto max-w-4xl p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (!permit) {
    return (
      <div className="mx-auto max-w-4xl p-8">
        <Breadcrumbs items={[{ label: t("title"), to: "/permits" }]} />
        <div className="mt-6 rounded-md bg-danger/10 p-4 text-danger">
          {t("unknownError", { ns: "common" })}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-8">
      <Breadcrumbs
        items={[
          { label: t("title"), to: "/permits" },
          { label: permit.permit_number, to: `/permits/${permitId}` },
          { label: t("form.editTitle") },
        ]}
      />
      <PageHeader title={t("form.editTitle")} />

      <div className="mt-6">
        <PermitForm
          mode="edit"
          initialValues={{
            permit_number: permit.permit_number,
            permit_type: permit.permit_type,
            facility_id: permit.facility_id,
            status: permit.status,
            issued_date: permit.issued_date ?? "",
            effective_date: permit.effective_date ?? "",
            expiration_date: permit.expiration_date ?? "",
            issuing_agency: permit.issuing_agency ?? "",
            description: permit.description ?? "",
          }}
          facilities={facilities}
          selectedFacilityName={permit.facility_name}
          isPending={updateMutation.isPending}
          onSubmit={(values) => {
            updateMutation.mutate(
              {
                permit_number: values.permit_number,
                status: values.status || undefined,
                issued_date: values.issued_date || undefined,
                effective_date: values.effective_date || undefined,
                expiration_date: values.expiration_date || undefined,
                issuing_agency: values.issuing_agency || undefined,
                description: values.description || undefined,
              },
              {
                onSuccess: () => {
                  toast.success(t("toast.updateSuccess"));
                  void navigate({
                    to: "/permits/$permitId",
                    params: { permitId },
                  });
                },
                onError: () => {
                  toast.error(t("toast.updateError"));
                },
              },
            );
          }}
        />
      </div>
    </div>
  );
}
